import {
  type AuthResponseData,
  getRefreshToken,
  HTTPStatus,
  isLoggedIn,
  LogSentryException,
  removeTokens,
  type ResponseAPI,
  ServiceError,
} from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { forwardedTrackingIDHeaderName, queryKeys, sessionStorageKeys } from "../config";
import { TOKEN_AUDIENCE, UMSrequestClient } from "../stores/requestClient";
import { AuthError, type AuthMethod, ServiceErrorCode, type SignupSource } from "../types";
import { saveLoggedInUser } from "../utils/authUtils";
import { useTracker } from "./useTracker";

export type AuthMethodRequestPayload = { email: string; redirectURL?: string | null };
export type AuthMethodResponseData =
  | { method: AuthMethod.Password }
  | { method: AuthMethod.SSO; url: string };

export const useAuthMethod = () => {
  return useMutation({
    mutationKey: ["auth-method"],
    mutationFn: async (payload: AuthMethodRequestPayload) => {
      const response = await UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/web/method",
        body: payload,
      });

      return response.json<AuthMethodResponseData>();
    },
  });
};

export type SSOCallbackRequestPayload = { code: string; authSessionID: string };
export type SSOCallbackResponseData = {
  userId: string;
  email: string;
  refreshToken: string;
  accessToken: string;
  teamName?: string;
  teamID?: string;
  redirectURL?: string;
};

export const useSSOCallback = () => {
  return useMutation({
    mutationKey: ["sso-callback"],
    mutationFn: async (payload: SSOCallbackRequestPayload) => {
      const response = await UMSrequestClient.publicRequest({
        method: "POST",
        url: "/sso/callback",
        body: payload,
      }).catch((error) => {
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.SSOAuthenticationEmailMismatch) ||
            error.hasCode(ServiceErrorCode.InvalidAuthenticationSession) ||
            error.hasCode(ServiceErrorCode.SSOAuthenticationSessionExpired) ||
            error.hasCode(ServiceErrorCode.SSOInvalidNameIDPolicy))
        ) {
          throw error;
        }

        LogSentryException({ error, messagePrefix: "SSO callback error" });
        throw error;
      });

      return response.json<SSOCallbackResponseData>();
    },
    onSuccess: (data) => {
      const { accessToken, email, refreshToken, userId } = data;
      saveLoggedInUser({ accessToken, email, refreshToken, id: userId });
    },
  });
};

export type RequestPasswordResetPayload = { email: string; captcha: string };

export const useRequestPasswordReset = () => {
  return useMutation<ResponseAPI, AuthError, RequestPasswordResetPayload>({
    mutationKey: ["request-password-reset"],
    mutationFn: async ({ email, captcha }: { email: string; captcha: string }) => {
      return UMSrequestClient.publicRequest({
        method: "POST",
        url: "/reset-password",
        body: { email, captcha },
      }).catch((error): never => {
        if (error instanceof ServiceError) {
          if (
            error.hasCode(ServiceErrorCode.MissingCaptcha) ||
            error.hasCode(ServiceErrorCode.CaptchaMissmatch)
          ) {
            throw AuthError.MissingCaptcha;
          }
          if (error.hasCode(ServiceErrorCode.InvalidEmail)) {
            throw AuthError.InvalidEmail;
          }
        }

        LogSentryException({ error });
        throw AuthError.Unknown;
      });
    },
  });
};

export const useResendVerificationEmail = () => {
  return useMutation({
    mutationKey: [...queryKeys.authenticatedQueryKey, "resend-verification-email"],
    mutationFn: async () => {
      return UMSrequestClient.authenticatedRequest({ method: "POST", url: "/verify/resend" });
    },
  });
};

export type ZendeskTokenResponseData = { zendeskToken: string };
export const useZendeskToken = () => {
  return useQuery({
    queryKey: [...queryKeys.authenticatedQueryKey, "zendesk-token"],
    queryFn: async () => {
      const response = await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: "/auth/zendesk/token",
      });

      return response.json<ZendeskTokenResponseData>();
    },
    staleTime: Infinity,
    enabled: isLoggedIn(),
  });
};

export const useVerifyAuthHandoff = () => {
  return useMutation({
    mutationKey: ["verify-auth-handoff"],
    mutationFn: async (authHandoffToken: string) => {
      const extraHeaders: { [key: string]: string } = {};

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        extraHeaders.Authorization = "Bearer " + refreshToken;
      }

      const response = await UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/handoff/verify",
        extraHeaders,
        body: { token: authHandoffToken, audience: TOKEN_AUDIENCE },
      });

      return response.json<AuthResponseData>();
    },
  });
};

export const useRequestAuthHandoffToken = () => {
  return useMutation({
    mutationKey: ["verify-auth-handoff"],
    mutationFn: async () => {
      const extraHeaders: { [key: string]: string } = {};

      const refreshToken = getRefreshToken();
      if (refreshToken) {
        extraHeaders.Authorization = "Bearer " + refreshToken;
      }

      const response = await UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/handoff",
        extraHeaders,
      });

      return response.json<{ token: string }>();
    },
  });
};

export const useCheckResetPasswordHash = ({
  confirmationHash,
}: {
  confirmationHash: string | undefined;
}) => {
  return useQuery({
    queryKey: ["check-reset-password-hash", confirmationHash],
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      return UMSrequestClient.publicRequest({
        method: "GET",
        url: `/reset-password/v2/${confirmationHash}`,
        abortSignal: signal,
      });
    },
    enabled: Boolean(confirmationHash),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export type ResetPasswordPayload = { password: string };
export const useResetPassword = ({
  confirmationHash,
}: {
  confirmationHash: string | undefined;
}) => {
  return useMutation<ResponseAPI, AuthError, ResetPasswordPayload>({
    mutationKey: ["reset-password", confirmationHash],
    mutationFn: async ({ password }: ResetPasswordPayload) => {
      if (!confirmationHash) {
        throw AuthError.Unknown;
      }

      return UMSrequestClient.publicRequest({
        method: "POST",
        url: `/reset-password/v2/${confirmationHash}`,
        body: { password },
      }).catch((error) => {
        if (error instanceof ServiceError) {
          if (error.hasCode(ServiceErrorCode.PasswordUnchanged)) {
            throw AuthError.PasswordUnchanged;
          }
        }

        LogSentryException({ error });
        throw AuthError.Unknown;
      });
    },
  });
};

export type RegisterRequestPayload = {
  email: string;
  password: string;
  name: string;
  captcha?: string;
  signupSource: SignupSource;
  isMobile: boolean;
};
export const useRegister = ({ appTrackingID }: { appTrackingID?: string }) => {
  const tracker = useTracker();

  return useMutation<void, AuthError, RegisterRequestPayload>({
    mutationKey: ["register"],
    mutationFn: async (payload: RegisterRequestPayload) => {
      if (isLoggedIn()) {
        tracker.reset();
        removeTokens();
      }

      const headers = {
        ...(appTrackingID && { [forwardedTrackingIDHeaderName]: appTrackingID }),
      };

      return UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/web/register",
        body: payload,
        extraHeaders: headers,
      })
        .then(async (res) => {
          const { accessToken, email, refreshToken, id, emailToken } =
            await res.json<AuthResponseData>();
          saveLoggedInUser({ accessToken, email, refreshToken, id });
          if (emailToken) {
            sessionStorage.setItem(sessionStorageKeys.emailToken, emailToken);
          }
          tracker.trackSignedUp(appTrackingID);
        })
        .catch((error): never => {
          if (error instanceof ServiceError) {
            if (error.hasStatusAndCode?.(HTTPStatus.BadRequest, ServiceErrorCode.MissingCaptcha)) {
              throw AuthError.MissingCaptcha;
            } else if (
              error.hasStatusAndCode(HTTPStatus.Conflict, ServiceErrorCode.EmailAlreadyUsed)
            ) {
              throw AuthError.EmailAlreadyUsed;
            } else if (
              error.hasStatusAndCode(HTTPStatus.BadRequest, ServiceErrorCode.InvalidEmail)
            ) {
              throw AuthError.InvalidEmail;
            } else if (
              error.hasStatusAndCode(HTTPStatus.BadRequest, ServiceErrorCode.WeakPassword)
            ) {
              throw AuthError.WeakPassword;
            } else if (
              error.hasStatusAndCode(HTTPStatus.BadRequest, ServiceErrorCode.TooLongPassword)
            ) {
              throw AuthError.TooLongPassword;
            }
          }

          LogSentryException({ messagePrefix: "Register error", error });
          throw AuthError.Unknown;
        });
    },
  });
};

export type LoginRequestPayload = {
  email: string;
  password: string;
  captcha?: string;
  twoFactorTotpCode?: string;
  twoFactorTotpRecoveryCode?: string;
};
export const useLogin = ({ appTrackingID }: { appTrackingID?: string } = {}) => {
  const tracker = useTracker();

  return useMutation<void, AuthError, LoginRequestPayload>({
    mutationKey: ["login"],
    mutationFn: async (payload: LoginRequestPayload) => {
      if (isLoggedIn()) {
        tracker.reset();
        removeTokens();
      }

      const headers = {
        ...(appTrackingID && { [forwardedTrackingIDHeaderName]: appTrackingID }),
      };

      return UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/web/login",
        extraHeaders: headers,
        body: payload,
      })
        .then(async (res) => {
          const responseJSON = await res.json<AuthResponseData>();
          saveLoggedInUser(responseJSON);
          tracker.trackLoggedIn(appTrackingID);
        })
        .catch((error): never => {
          if (error instanceof ServiceError) {
            if (
              error.hasStatusAndCode?.(HTTPStatus.BadRequest, ServiceErrorCode.MissingCaptcha) ||
              error.hasStatusAndCode?.(HTTPStatus.BadRequest, ServiceErrorCode.CaptchaMissmatch)
            ) {
              throw AuthError.MissingCaptcha;
            } else if (
              error.hasStatusAndCode?.(
                HTTPStatus.BadRequest,
                ServiceErrorCode.TwoFactorTotpCodeRequired
              )
            ) {
              throw AuthError.TOTPRequired;
            } else if (error.message === HTTPStatus.NotFound) {
              throw AuthError.InvalidEmailPassword;
            } else if (error.message === HTTPStatus.TooManyRequests) {
              throw AuthError.RateLimit;
            }
          }

          LogSentryException({ messagePrefix: "Login error", error });
          throw AuthError.Unknown;
        });
    },
  });
};

export const useLogout = (
  { redirect, withRequest }: { redirect: boolean; withRequest: boolean } = {
    redirect: true,
    withRequest: true,
  }
) => {
  const tracker = useTracker();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["logout"],
    mutationFn: async () => {
      if (!withRequest) {
        return;
      }

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        return;
      }

      return UMSrequestClient.publicRequest({
        method: "POST",
        url: "/auth/logout",
        extraHeaders: {
          Authorization: `Bearer ${refreshToken}`,
        },
      });
    },
    onSettled: () => {
      tracker.reset();
      removeTokens();
      queryClient.setQueriesData({ queryKey: queryKeys.authenticatedQueryKey }, undefined);
      queryClient.removeQueries({ queryKey: queryKeys.authenticatedQueryKey });

      if (redirect) {
        window.location.assign("https://www.shapr3d.com/");
      }
    },
  });
};
